import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import {
  loadBuckets,
  loadStatus,
  searchBuckets,
  isLive,
} from '../../api/buckets.js';
import { starColor, glow, starSize, pulseDuration, isHeartbeat } from '../../lib/star.js';
import MemoryLeaf from '../../components/MemoryLeaf.jsx';
import { useFavs } from '../../lib/favs.js';
import './StarMap.css';

const VIEWS = { LIST: 'list', MAP: 'map' };

/* 10-04 铃拍板「海报感」改版（示范页 public/memory-demo.html 定稿）：
   拉丁小标题 + 罗马数字 + 钉着的小纸条 + 右上一串手绘吊灯 + 罗纹纸底，
   列表=一根星光丝线上挂着的星（越重要越亮，快被忘掉的真的变浅）。
   这页有自己的白天/黑夜（跟心跳页一样不喝站点主题），黑夜里灯泡一颗颗亮。 */
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
const MONTH_LA = ['IANVARIVS', 'FEBRVARIVS', 'MARTIVS', 'APRILIS', 'MAIVS', 'IVNIVS', 'IVLIVS',
  'AVGVSTVS', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
const MONTH_CN = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二'];
function romanYear(y) {
  const map = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
    [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
  let out = '';
  for (const [n, r] of map) while (y >= n) { out += r; y -= n; }
  return out;
}
// 记忆的亮度 0~1：衰减分(score)按对数折算；恒星/固化(999)=最亮
function brightness(b) {
  if (b.pinned || b.weight >= 999) return 1;
  const w = Math.max(0, Number(b.weight) || 0);
  return Math.max(0.08, Math.min(1, Math.log10(w + 1) / Math.log10(21)));
}
// 灯泡位置（占吊灯图宽/高的 %），点亮顺序从上往下
const BULBS = [[17.5, 35], [45.3, 17.5], [50.8, 34.1], [71.3, 33.8], [88, 21.7], [35.8, 49.5],
  [30, 57.4], [75.8, 48.1], [87.5, 51.4], [63.7, 61.8], [51.7, 66.1], [84.2, 80.3], [71.7, 92],
  [84.7, 99]].sort((a, b) => a[1] - b[1]);
const NIGHT_KEY = 'lingke.memory.night';

export default function StarMap() {
  const [buckets, setBuckets] = useState([]);
  const [status, setStatus] = useState(null);
  const [live, setLive] = useState(false);
  const [view, setView] = useState(VIEWS.LIST);
  const [q, setQ] = useState('');
  const [filtered, setFiltered] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  // 珍藏（2026-10-16 铃）：拉一下页上那根丝带 = 只看她夹了书签的
  const favs = useFavs();
  const [favOnly, setFavOnly] = useState(false);
  // 白天/黑夜：记住她上次选的；没选过按钟点
  const [night, setNight] = useState(() => {
    try {
      const v = localStorage.getItem(NIGHT_KEY);
      if (v === '1' || v === '0') return v === '1';
    } catch {}
    // 10-04 铃：没手动选过就默认白天（原来按钟点，晚上一打开就是黑夜）
    return false;
  });
  const toggleNight = () =>
    setNight((n) => {
      try { localStorage.setItem(NIGHT_KEY, n ? '0' : '1'); } catch {}
      return !n;
    });
  const searchTimer = useRef();
  // 10-17 铃：「搜出来的记忆点回车/一滑就全没了，有时会有时不会」。
  // 真凶=中文输入法打字时框里先是拼音(suan)，拼音那次搜索去大脑要「意思相近」走网络，
  // 4G 上慢一两秒才回来，盖掉了「算」已经搜好的结果。修：①拼字没打完(composition)不搜；
  // ②只认最后一次搜索的结果，旧的回来了直接扔掉。
  const composing = useRef(false);
  const [term, setTerm] = useState('');
  // 「星图要点好几下」（2026-07-19 铃报）：TabBar bcd2b34 同款病——列表长、
  // 惯性滚动没停时那下点按被 iOS 当刹车吞掉 click。同款药：pointerup 即切换
  // （位移>12px 视为拖动不切），click 留作键盘/老环境兜底（重复 setView 无害）。
  const tabPressRef = useRef(null);
  const tabDown = (e) => {
    tabPressRef.current = { x: e.clientX, y: e.clientY };
  };
  const tabUp = (v) => (e) => {
    const p = tabPressRef.current;
    tabPressRef.current = null;
    if (!p) return;
    if (Math.abs(e.clientX - p.x) > 12 || Math.abs(e.clientY - p.y) > 12) return;
    setView(v);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [list, s] = await Promise.all([loadBuckets(), loadStatus()]);
      if (cancelled) return;
      list.sort(
        (a, b) =>
          (b.pinned - a.pinned) ||
          (b.weight - a.weight) ||
          (b.importance - a.importance),
      );
      setBuckets(list);
      setStatus(s);
      setLive(isLive());
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 搜索去抖
  useEffect(() => {
    clearTimeout(searchTimer.current);
    if (!term.trim()) {
      setFiltered(null);
      return undefined;
    }
    let stale = false;
    searchTimer.current = setTimeout(async () => {
      const res = await searchBuckets(term, buckets);
      if (!stale) setFiltered(res);
    }, 220);
    return () => {
      stale = true;
      clearTimeout(searchTimer.current);
    };
  }, [term, buckets]);

  // ESC 关闭详情
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenId(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const all = filtered ?? buckets;
  const items = useMemo(() => (favOnly ? all.filter((b) => favs.has(b.bucket_id)) : all), [all, favOnly, favs]);
  const favCount = useMemo(() => buckets.filter((b) => favs.has(b.bucket_id)).length, [buckets, favs]);
  // 纸底下翻上一条/下一条：和列表看到的顺序一样（恒星在前，其余新→旧；搜索时按结果顺序）
  const itemsOrder = useMemo(() => {
    if (filtered) return items.map((b) => b.bucket_id);
    const rest = items
      .filter((b) => !b.pinned)
      .sort((x, y) => String(y.created_at || '').localeCompare(String(x.created_at || '')));
    return [...items.filter((b) => b.pinned), ...rest].map((b) => b.bucket_id);
  }, [items, filtered]);

  // 删除成功后：从列表（和搜索结果）里移除这颗星并合上抽屉
  const handleDeleted = (id) => {
    setBuckets((bs) => bs.filter((x) => x.bucket_id !== id));
    setFiltered((f) => (f ? f.filter((x) => x.bucket_id !== id) : f));
    setOpenId(null);
  };

  return (
    <div className={'sm-wrap mp ' + (night ? 'mp-night' : 'mp-day')}>
      <div className="mp-backdrop" aria-hidden="true" />
      <div className="mp-lamps" aria-hidden="true">
        <img src="/img/memory/lamps.webp?v=2" alt="" />
        {BULBS.map(([x, y], k) => (
          <i key={k} style={{ left: `${x}%`, top: `${y}%`, '--d': k }} />
        ))}
      </div>

      <header className="mp-head">
        <div className="mp-kicker">{favOnly ? 'AMATA · 珍 藏' : 'MEMORIA · 记 忆'}</div>
        {favOnly ? (
          <div className="mp-script">
            the pages
            <br />
            she keeps
            <br />
            close to her heart
          </div>
        ) : (
          <div className="mp-script">
            where our
            <br />
            memories
            <br />
            keep their light
          </div>
        )}
        <div className="mp-rule">✦</div>
        <div className="mp-date">
          <b>{ROMAN[new Date().getMonth()]} · {MONTH_LA[new Date().getMonth()]} · {romanYear(new Date().getFullYear())}</b> · lux perpetua
        </div>
        <StatusNote status={status} items={buckets} />
      </header>

      <div className="mp-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={view === VIEWS.LIST}
          className={'mp-tab' + (view === VIEWS.LIST ? ' on' : '')}
          onPointerDown={tabDown}
          onPointerUp={tabUp(VIEWS.LIST)}
          onClick={() => setView(VIEWS.LIST)}
        >
          列 表<small>INDEX</small>
        </button>
        <button
          role="tab"
          aria-selected={view === VIEWS.MAP}
          className={'mp-tab' + (view === VIEWS.MAP ? ' on' : '')}
          onPointerDown={tabDown}
          onPointerUp={tabUp(VIEWS.MAP)}
          onClick={() => setView(VIEWS.MAP)}
        >
          星 图<small>ASTRA</small>
        </button>
        {live && (
          <button
            type="button"
            className={'mp-ribbon' + (favOnly ? ' on' : '')}
            onClick={() => setFavOnly((v) => !v)}
            aria-pressed={favOnly}
            aria-label={favOnly ? '回到全部记忆' : `她的珍藏（${favCount} 篇）`}
          >
            <small>AMATA</small>
            <svg viewBox="0 0 16 44" aria-hidden="true">
              <path className="mp-ribbon-silk" d="M1 0h14v44l-7-6-7 6z" />
              <path className="mp-ribbon-heart" d="M8 31c-2.7-1.9-3.9-3.3-3.9-4.7 0-1.1.9-2 2-2 .8 0 1.5.4 1.9 1.1.4-.7 1.1-1.1 1.9-1.1 1.1 0 2 .9 2 2 0 1.4-1.2 2.8-3.9 4.7z" />
            </svg>
          </button>
        )}
        <button
          type="button"
          className={'mp-icon' + (searchOpen || q ? ' on' : '')}
          onClick={() => {
            if (searchOpen) {
              setQ('');
              setTerm('');
            }
            setSearchOpen((o) => !o);
          }}
          aria-label="在记忆里寻找"
        >
          {searchOpen ? <X size={16} strokeWidth={1.5} /> : <Search size={16} strokeWidth={1.5} />}
        </button>
        <button type="button" className="mp-icon" onClick={toggleNight} aria-label="白天/黑夜">
          {night ? '☀' : '☾'}
        </button>
      </div>

      {searchOpen && (
        <div className="mp-search now-focus">
          <Search size={14} strokeWidth={1.5} />
          <input
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              // 以浏览器自己报的为准（万一 compositionend 没来，也不会一直卡着不搜）
              const c = e.nativeEvent.isComposing ?? composing.current;
              composing.current = !!c;
              if (!c) setTerm(e.target.value);
            }}
            onCompositionStart={() => {
              composing.current = true;
            }}
            onCompositionEnd={(e) => {
              composing.current = false;
              setTerm(e.target.value);
            }}
            placeholder="在记忆里寻找……"
            autoComplete="off"
            autoFocus
            aria-label="搜索记忆"
          />
        </div>
      )}

      {filtered && (
        <div className="mp-favline">
          {filtered.some((b) => !b.near)
            ? <>找到 <b>{filtered.filter((b) => !b.near).length}</b> 篇写到「{term.trim()}」的</>
            : <>没有哪篇原话写到「{term.trim()}」{filtered.length ? '，下面是意思相近的' : ''}</>}
        </div>
      )}
      {favOnly && (
        <div className="mp-favline">
          她珍藏了 <b>{favCount}</b> 篇 · 点开一篇，轻点纸顶的丝带就能夹上或取下
        </div>
      )}

      <div className="sm-view">
        {loading ? (
          <Loading />
        ) : view === VIEWS.LIST ? (
          favOnly && !items.length ? (
            <Empty text={filtered ? '珍藏里没有找到这句 ✦' : '还没有夹书签的记忆——点开一篇，轻点纸顶的丝带 ✦'} />
          ) : (
            <ListView items={items} onOpen={setOpenId} searching={!!filtered} />
          )
        ) : (
          <MapView items={items} onOpen={setOpenId} />
        )}
      </div>

      <footer className="sm-footer">每一条记忆，都是一颗不肯熄灭的星。</footer>

      <MemoryLeaf
        id={openId}
        order={itemsOrder}
        onNav={setOpenId}
        cache={buckets}
        onClose={() => setOpenId(null)}
        live={live}
        onDeleted={handleDeleted}
      />

      <SourceBadge live={live} />
    </div>
  );
}

/* ====================== StatusNote：钉在墙上的小纸条 ====================== */
function StatusNote({ status, items }) {
  const solid =
    status?.solid != null ? status.solid : items.filter((b) => b.type === 'solid').length;
  const dynamic =
    status?.dynamic != null
      ? status.dynamic
      : items.filter((b) => b.type !== 'solid' && b.type !== 'archived').length;
  const archived =
    status?.archived != null
      ? status.archived
      : items.filter((b) => b.type === 'archived').length;
  const engineOn = status ? status.engine !== false : true;

  return (
    <div className="mp-note">
      <div className="mp-note-k">HODIE · 今 天</div>
      <div className="mp-note-big">
        {solid + dynamic}
        <i>颗记忆亮着</i>
      </div>
      <div className="mp-note-r">
        <span>FIXA</span>固化 {solid} · 动态 {dynamic} · 归档 {archived}
      </div>
      <div className={'mp-note-r' + (engineOn ? '' : ' offline')}>
        <span>MOTVS</span>衰减引擎
        <em>● {engineOn ? '运行中' : '停止'}</em>
      </div>
    </div>
  );
}

/* ====================== ListView：一根星光丝线 ======================
   恒星（钉住的）挂最上面一段；其余按月份从新到旧，一月一段。
   搜索时不分段，按搜索结果的相关度顺序挂。 */
function ListView({ items, onOpen, searching }) {
  if (!items.length) return <Empty />;
  const groups = [];
  if (searching) {
    const exact = items.filter((b) => !b.near);
    const near = items.filter((b) => b.near);
    if (exact.length) groups.push({ key: 'q', head: null, rows: exact });
    if (near.length) groups.push({ key: 'near', head: { b: '≈', cn: '意思相近的', la: 'SIMILIA' }, rows: near });
  } else {
    const fixed = items.filter((b) => b.pinned);
    if (fixed.length) groups.push({ key: 'fixed', head: { b: '✦', cn: '恒星', la: 'STELLAE FIXAE' }, rows: fixed });
    const rest = items
      .filter((b) => !b.pinned)
      .slice()
      .sort((x, y) => String(y.created_at || '').localeCompare(String(x.created_at || '')));
    for (const b of rest) {
      const d = b.created_at ? new Date(b.created_at) : null;
      const ok = d && !isNaN(d);
      const key = ok ? `${d.getFullYear()}-${d.getMonth()}` : 'olim';
      let g = groups[groups.length - 1];
      if (!g || g.key !== key) {
        g = {
          key,
          head: ok
            ? { b: ROMAN[d.getMonth()], cn: `${MONTH_CN[d.getMonth()]}月`, la: `${MONTH_LA[d.getMonth()]} ${d.getFullYear()}` }
            : { b: '·', cn: '更早', la: 'OLIM' },
          rows: [],
        };
        groups.push(g);
      }
      g.rows.push(b);
    }
  }
  let n = 0;
  return (
    <div className="mp-thread">
      {groups.map((g) => (
        <section key={g.key} className="mp-group">
          {g.head && (
            <div className="mp-month">
              <b>{g.head.b}</b>
              {g.head.cn}
              <small>{g.head.la}</small>
            </div>
          )}
          {g.rows.map((b) => {
            const w = brightness(b);
            const heart = isHeartbeat(b);
            const d = b.created_at ? new Date(b.created_at) : null;
            const day = d && !isNaN(d) ? `${d.getMonth() + 1}月${d.getDate()}日` : '';
            const i = n++;
            return (
              <button
                key={b.bucket_id}
                type="button"
                className={'mp-star' + (b.pinned ? ' eternal' : '') + (heart ? ' is-heart' : '')}
                style={{
                  '--s': `${8 + 10 * w}px`,
                  '--o': 0.38 + 0.62 * Math.min(1, w * 1.4),
                  '--glow': w,
                  animationDelay: `${Math.min(i, 12) * 0.05}s`,
                }}
                onClick={() => onOpen(b.bucket_id)}
              >
                <span className="mp-dot" aria-hidden="true" />
                <span className="mp-t">
                  <span className="mp-t-name">{b.title}</span>
                  {b.pinned && <em>恒星</em>}
                </span>
                <span className="mp-m">
                  {b.domain.length > 0 && <i>{b.domain.join(' · ')}</i>}
                  {b.domain.length > 0 && day && ' · '}
                  {day}
                </span>
              </button>
            );
          })}
        </section>
      ))}
    </div>
  );
}

/* ====================== MapView ====================== */
function MapView({ items, onOpen }) {
  // 按重要度分层：越重要越靠中心；用黄金角散布
  const stars = useMemo(() => {
    return items.map((b, i) => {
      const imp = Math.max(1, Math.min(10, b.importance));
      const radius = 8 + (10 - imp) * 4.2;
      const ang = ((i * 137.508) * Math.PI) / 180;
      const jx = (Math.sin(i * 12.9) + 1) / 2 * 5;
      return {
        b,
        x: 50 + Math.cos(ang) * radius + (jx - 2.5),
        y: 48 + Math.sin(ang) * radius * 0.92,
      };
    });
  }, [items]);

  if (!items.length) return <Empty />;

  return (
    <div className="sm-starmap">
      {stars.map(({ b, x, y }) => {
        const col = starColor(b.valence);
        const size = starSize(b.importance);
        const g = glow(b.weight) * 0.8 + b.arousal * 8;
        const pd = pulseDuration(b.arousal);
        const heart = isHeartbeat(b);
        return (
          <button
            key={b.bucket_id}
            className={'sm-mstar' + (b.pinned ? ' is-pin' : '') + (heart ? ' is-heart' : '')}
            style={{
              left: `${x}%`,
              top: `${y}%`,
              width: `${size}px`,
              height: `${size}px`,
              '--sc': col,
              '--sg': `${g}px`,
              '--pd': `${pd}s`,
            }}
            onClick={() => onOpen(b.bucket_id)}
            aria-label={b.title}
          >
            <span className="sm-mstar-core" />
            <span className="sm-mstar-lbl">{b.title}</span>
          </button>
        );
      })}
      <div className="sm-map-hint">轻点一颗星，翻开那段记忆</div>
    </div>
  );
}

/* ====================== Common ====================== */
function Loading() {
  return (
    <div className="sm-loading">
      <span className="sm-o" />
      <span className="sm-o" />
      <span className="sm-o" />
    </div>
  );
}

function Empty({ text = '这片夜空还没有星 ✦' }) {
  return <div className="sm-empty">{text}</div>;
}

function SourceBadge({ live }) {
  return (
    <div className={'sm-source' + (live ? ' is-live' : '')}>
      ✦ {live ? '实时记忆 · ' : '示例记忆 · '}
      <b>{live ? '已连接' : '未连接后端'}</b>
    </div>
  );
}
